import { useMemo } from 'react';
import CustomStore from 'devextreme/data/custom_store';
import { apiInstance } from '../context/api';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { RequestMethod } from '../context/api/types';

export interface StoreConfig<Item> {
  url: string;
  keyField: keyof Item;
  dataSourceKey?: string;
  loadUrl?: string;
  insertUrl?: string;
  updateUrl?: string;
  removeUrl?: string;
}

export const crudRequest = async (
  reqOptions: {
    method: RequestMethod,
    url: string,
    dataSourceKey: string
  },
  values?: unknown
) => {
  try {
    const res: { [key: string]: any } = await apiInstance[reqOptions.method](reqOptions.url, values || undefined);

    let returned = res[reqOptions.dataSourceKey];

    if (reqOptions.method !== 'get') {
      returned = res.data;
      toast.success(_.get(res, 'msg', 'Saved successfully'));
    }

    return returned;
  } catch (error) {
    toast.error(
      _.get(
        error, 'error.message', 'Error while saving. Please check the parameters'
      ));
    handleApiError(error);
  }
};

export function useDxStore<Item extends Record<string, any>>(
  config: StoreConfig<Item>
) {
  const {
    dataSourceKey = 'items',
    url,
    keyField,
    loadUrl = '',
    insertUrl = '',
    updateUrl = '',
    removeUrl = '',
  } = config;
  const store = useMemo(() => new CustomStore({
    key: keyField as string,
    load: () => crudRequest({
      method: 'get',
      url: loadUrl ? loadUrl : `${url}`,
      dataSourceKey
    }),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    insert: (values: Partial<Item>) => {
      return crudRequest({
        method: 'post',
        url: insertUrl ? insertUrl : `${url}`,
        dataSourceKey
      }, values)
    },
    update: (key: any, values: Partial<Item>) => {
      return crudRequest({
        method: 'post',
        url: updateUrl ? `${updateUrl}/${key}` : `${url}/${key}`,
        dataSourceKey
      }, values)
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    remove: (key: any) => {
      return crudRequest({
        method: 'delete',
        url: removeUrl ? `${removeUrl}/${key}` : `${url}/${key}`,
        dataSourceKey
      })
    },
  }), [
    dataSourceKey,
    keyField,
    url,
    loadUrl,
    insertUrl,
    updateUrl,
    removeUrl
  ]);

  return store;
}

const errorFields: any = {
  blobRegion: 'Region: ',
  blobUrl: 'URL: ',
  type: 'Type: '
}

function handleApiError(err: any): never {
  const payload = err?.error;
  if (payload) {
    if (payload.type === 'validation' && payload.validationErrors) {
      const ve = payload.validationErrors;
      const firstField = Object.keys(ve)[0];
      const firstError = ve[firstField];
      const firstCode = Object.keys(firstError)[0];
      const fieldName = errorFields[firstField] ? errorFields[firstField] : '';
      const msg = fieldName  + firstError[firstCode];
      throw new Error(msg);
    }
    throw new Error(payload.message || 'Server error');
  }
  throw new Error(err.error.message || 'Unknown network error');
}
